import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import CloseIcon from '/public/close-2.svg';
import RightIcon from '/public/right-arrow.svg';
import { useI18n } from '@/hooks/use-i18n';
import { ActionButtonGroup } from '@/components/ui/action-button-group';
import { useImageActionButtons } from '@/hooks/deep-mining/use-image-action-buttons';
import ImageError from '@/components/thread/dm-tool-views/image-batch-edit-tool-view/image-error';

// 修改函数签名,支持单图和多图
export const previewImage = (
  url: string | string[],
  canDownload: boolean = false,
  initialIndex: number = 0,
) => {
  // 统一转换为数组格式
  const urls = Array.isArray(url) ? url : [url];

  if (!urls.length || !urls[0]) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent('image-preview', {
      detail: { urls, canDownload, initialIndex },
    }),
  );
};

// 图片预览组件
const ImagePreview = () => {
  const { t } = useI18n('file');

  // 使用数组存储图片（支持多图）
  const [images, setImages] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [style, setStyle] = useState<string>('');
  const [canDownload, setCanDownload] = useState<boolean>(false);
  const [imageError, setImageError] = useState<boolean>(false);

  const onClose = useCallback(() => {
    setImages([]);
    setCurrentIndex(0);
    setImageError(false);
  }, []);

  useEffect(() => {
    const handlePreviewImage = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        const {
          urls,
          style = '',
          canDownload,
          initialIndex = 0,
        } = customEvent.detail;

        if (!urls || !urls.length) {
          return;
        }

        setStyle(style);
        setImages(urls);
        setCurrentIndex(initialIndex);
        setCanDownload(canDownload);
      }
    };

    // Add event listener
    window.addEventListener(
      'image-preview',
      handlePreviewImage as EventListener,
    );

    // Cleanup
    return () => {
      window.removeEventListener(
        'image-preview',
        handlePreviewImage as EventListener,
      );
    };
  }, []);

  // 使用 useEffect 在组件挂载时阻止背景滚动，并监听键盘事件
  useEffect(() => {
    if (images.length === 0) return;

    // 保存原始的 overflow 样式
    const originalStyle = window.getComputedStyle(document.body).overflow;
    // 禁止背景滚动
    document.body.style.overflow = 'hidden';

    // 处理键盘事件
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        // 左键：上一张
        if (currentIndex > 0) {
          setCurrentIndex(currentIndex - 1);
          setImageError(false);
        }
      } else if (e.key === 'ArrowRight') {
        // 右键：下一张
        if (currentIndex < images.length - 1) {
          setCurrentIndex(currentIndex + 1);
          setImageError(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // 组件卸载时恢复原始样式并移除事件监听
    return () => {
      document.body.style.overflow = originalStyle;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [images, currentIndex]);

  // 当前显示的图片URL
  const currentImageUrl = images[currentIndex] || '';

  // 当图片URL变化时，重置错误状态
  useEffect(() => {
    setImageError(false);
  }, [currentImageUrl]);

  // 图片操作按钮配置（使用原有的 useImageActionButtons）
  const { buttons: imageActionButtons } = useImageActionButtons({
    url: currentImageUrl,
    showDownload: canDownload,
    onAfterOpenInCanvas: onClose, // 成功打开画布后关闭预览
  });

  // 没有图片时不渲染
  if (images.length === 0) {
    return null;
  }

  // 是否是多图模式
  const isMultipleImages = images.length > 1;

  // 切换到上一张
  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setImageError(false);
    }
  };

  // 切换到下一张
  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setImageError(false);
    }
  };

  // 处理图片加载失败
  const handleImageError = () => {
    setImageError(true);
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center pointer-events-auto"
      style={{ zIndex: 9999, backgroundColor: 'rgba(0,0,0,.6)' }}
      onClick={(e) => {
        // 只有点击背景才关闭
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="relative">
        <button
          type="button"
          className="fixed top-6 right-6 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <CloseIcon size={24} />
        </button>

        <div className="relative">
          {/* 图片容器 - 固定容器尺寸，图片在内部自适应 */}
          <div
            className="relative flex items-center justify-center"
            style={
              isMultipleImages
                ? {
                    width: 'calc(100vw - 300px)',
                    height: 'calc(100vh - 120px)',
                    minWidth: '300px',
                    minHeight: '300px',
                  }
                : {
                    minWidth: '90vw',
                    minHeight: '90vh',
                  }
            }
          >
            {/* 包裹图片和按钮，确保按钮相对于图片定位 */}
            {!imageError && (
              <div className="relative max-w-full max-h-full">
                <img
                  src={currentImageUrl}
                  alt={t('image.imagePreviewAlt')}
                  className={cn('object-contain bg-[#fff]', style)}
                  style={
                    isMultipleImages
                      ? { maxWidth: '100%', maxHeight: 'calc(100vh - 120px)' }
                      : { maxWidth: '90vw', maxHeight: '90vh' }
                  }
                  onError={handleImageError}
                />

                {/* 操作按钮组 - 相对于图片右上角定位，距离右边16px，上边16px */}
                <div className="absolute top-4 right-4 bg-[#00000059] backdrop-blur-[4px] rounded-[8px] text-[#FFFFFF] h-8 p-1">
                  <ActionButtonGroup buttons={imageActionButtons} />
                </div>
              </div>
            )}

            {/* 图片加载失败提示 - 使用整个容器居中显示 */}
            {imageError && (
              <div className="absolute inset-0 flex items-center justify-center">
                <ImageError />
              </div>
            )}
          </div>

          {/* 多图轮播控制器（新功能） */}
          {isMultipleImages && (
            <>
              {/* 左箭头按钮 - 图片左侧居中 */}
              <div
                className={cn(
                  'fixed left-8 top-1/2 transform -translate-y-1/2 w-[100px] h-[100px] flex items-center justify-center',
                  currentIndex === 0
                    ? 'opacity-40 cursor-not-allowed'
                    : 'cursor-pointer',
                )}
                onClick={handlePrevious}
              >
                <RightIcon className="w-[100px] h-[100px] text-white -rotate-180" />
              </div>

              {/* 右箭头按钮 - 图片右侧居中 */}
              <div
                className={cn(
                  'fixed right-8 top-1/2 transform -translate-y-1/2 w-[100px] h-[100px] flex items-center justify-center',
                  currentIndex === images.length - 1
                    ? 'opacity-40 cursor-not-allowed'
                    : 'cursor-pointer',
                )}
                onClick={handleNext}
              >
                <RightIcon className="w-[100px] h-[100px] text-white " />
              </div>

              {/* 页码指示器 - 底部居中，距离底部 16px */}
              <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-[#00000059] backdrop-blur-[4px] rounded-[8px] px-3 py-1.5">
                <span className="text-white text-[14px] font-medium">
                  {currentIndex + 1}/{images.length}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ImagePreview;
